import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { NeedToKnow, NeedToKnowKeys, NeedToKnowVisibilityLevel, ChangeSourceType } from '@ag-common-lib/public-api';
import { map } from 'rxjs/operators';
import { FormChangesDetector } from '../../../../../../shared/utils';
import { confirm } from 'devextreme/ui/dialog';
import { AgentNeedToKnowService } from '../../../../../services/agent-need-to-know.service';
import { AuthService } from 'ag-common-svc/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

@Injectable()
export class NeedToKnowModalService {
  public formData: NeedToKnow;
  public hasFormChanges$: Observable<boolean>;
  public readonly formChangesDetector: FormChangesDetector = new FormChangesDetector();

  public inProgress$: Observable<boolean>;
  private readonly _inProgress$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private agentNeedToKnowService: AgentNeedToKnowService,
  ) {
    this.inProgress$ = this._inProgress$.asObservable();
    this.hasFormChanges$ = this.formChangesDetector.actions$.pipe(
      map(() => {
        return this.formChangesDetector.hasChanges;
      }),
    );
  }

  public saveNeedToKnow = (agentId: string, action: ChangeSourceType | null = null) => {
    return this.formData[BaseModelKeys.dbId]
      ? this.updateNeedToKnow(agentId, action)
      : this.createNeedToKnow(agentId, action);
  };

  public onCancelEditNeedToKnow = ({ event, component }) => {
    if (!this.formChangesDetector?.hasChanges) {
      return;
    }

    event.cancel = true;

    const result = confirm('<i>Are you sure you want to Cancel without Saving?</i>', 'Confirm');
    result.then(dialogResult => {
      if (dialogResult) {
        this.formChangesDetector?.clear();
        component.instance.hide();
      }
    });
  };

  public getFormData = async (association?: Partial<NeedToKnow>) => {
    const loggedInUserId = await firstValueFrom(
      this.authService.loggedInAgent$.pipe(map(agent => agent?.[BaseModelKeys.dbId])),
    );
    const loggedInUserEmail = await firstValueFrom(
      this.authService.currentUser$.pipe(
        map(user => {
          console.log('User ', user);
          return user.email;
        }),
      ),
    );
    const initialData = Object.assign(
      {
        [BaseModelKeys.createdDate]: new Date(),
        [BaseModelKeys.createdByAgentDbId]: loggedInUserId,
        [BaseModelKeys.createdBy]: loggedInUserEmail,
        [NeedToKnowKeys.visibilityLevel]: NeedToKnowVisibilityLevel.AllianceGroupLevel,
      },
      new NeedToKnow(),
      association,
    );
    this.formData = new Proxy(initialData, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];
        this.formChangesDetector.handleChange(prop, value, prevValue);
        Reflect.set(target, prop, value, receiver);

        return true;
      },
    });

    return this.formData;
  };

  private createNeedToKnow = (agentId: string, action: ChangeSourceType | null = null) => {
    this._inProgress$.next(true);
    return this.agentNeedToKnowService
      .create(agentId, this.formData, false, action)
      .then(() => {
        this.formChangesDetector.clear();
      })
      .finally(() => {
        this._inProgress$.next(false);
      });
  };

  private updateNeedToKnow = (agentId: string, action: ChangeSourceType | null = null) => {
    this._inProgress$.next(true);
    return this.agentNeedToKnowService
      .update(agentId, this.formData[BaseModelKeys.dbId], this.formData, false, action)
      .then(() => {
        this.formChangesDetector.clear();
      })
      .finally(() => {
        this._inProgress$.next(false);
      });
  };
}
